/**
 * UltimateSetupModal.js
 *
 * 3-phase modal that drives the modular Ultimate Setup pipeline:
 *
 *   Phase 1 — INPUT
 *     One large textarea ("info dump") + 3 optional hint fields
 *     (name / role / company) + language override.
 *
 *   Phase 2 — LIVE PROGRESS
 *     Polls /World/${uid}/setupRuns/${runId} every 1.5s; shows step list with
 *     status icons, running cost, and an expandable "Why?" block per step
 *     that reveals the Opus thinking summary for the two reasoning steps.
 *
 *   Phase 3 — PREVIEW
 *     Cards for vault, cleaner, safeGuard, template — each with a deep-link
 *     into the corresponding settings tab. The template deep-link uses the
 *     ?topicId URL param that WorldSettingsTopics.js already reads.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../utils/cloudFunctions';
import { buildVaultSeedRawInfo } from '../../utils/vaultSeedForUltimateSetup';
// Global SpeakEngine — lets the user dictate the whole setup dump instead
// of typing it. The `businessSetup` preset extracts every field we render
// on this phase (rawInfo, companyName, name, role, language, targetSize).
import { useSpeakEngine } from '../../speakEngine';
import './UltimateSetupModal.css';

const STEPS_ORDER = [
  { id: 'profile',   label: 'Understanding your business',     icon: 'fa-brain' },
  { id: 'vault',     label: 'Saving long-term brain',          icon: 'fa-database' },
  { id: 'cleaner',   label: 'Building Keep/Remove guidelines', icon: 'fa-filter' },
  { id: 'safeGuard', label: 'Configuring SafeGuard',           icon: 'fa-shield-halved' },
  { id: 'strategy',  label: 'Designing 3 email styles',        icon: 'fa-pen-ruler' },
  { id: 'variants',  label: 'Generating email texts',          icon: 'fa-pen-to-square' },
  { id: 'refine',    label: 'Polishing template syntax',       icon: 'fa-spell-check' },
  { id: 'persist',   label: 'Saving everything',               icon: 'fa-floppy-disk' },
  { id: 'leads',     label: 'Researching matching leads',      icon: 'fa-user-plus' }, // Researcher-Quelle bewusst NICHT im Label; UI bleibt agnostisch zum Provider.
];

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'de', label: 'German' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
];

// Drives Grok lead-research after the setup. Each value maps to a stable
// employee-count band that the Grok prompt and downstream cleaner can both
// understand. Order from "auto" (Grok decides from target description) down
// to enterprise.
const COMPANY_SIZE_OPTIONS = [
  { value: '',          label: 'Auto-detect from description' },
  { value: 'solo',      label: 'Solo / freelancer (1 person)' },
  { value: 'micro',     label: 'Micro (2–10 employees)' },
  { value: 'small',     label: 'Small (11–50 employees)' },
  { value: 'mid',       label: 'Mid-size (51–250 employees)' },
  { value: 'large',     label: 'Large (251–1000 employees)' },
  { value: 'enterprise',label: 'Enterprise (1000+ employees)' },
];

const MIN_RAW = 150;

export default function UltimateSetupModal({ userId, onClose, onOpenTab, mode = 'modal', foundationDiagnostics = null }) {
  /** bootstrap → vault check | input → form | live → progress | preview → summary (skipped when hands-free) */
  const [phase, setPhase] = useState('bootstrap');
  const [rawInfo, setRawInfo] = useState('');
  const [hintName, setHintName] = useState('');
  const [hintRole, setHintRole] = useState('');
  const [hintCompany, setHintCompany] = useState('');
  const [hintLanguage, setHintLanguage] = useState('');
  const [hintTargetSize, setHintTargetSize] = useState('');

  const [runId, setRunId] = useState(null);
  const [runState, setRunState] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);
  /** True once we loaded seed text from Account Vault into rawInfo */
  const [vaultPrefilled, setVaultPrefilled] = useState(false);

  /** When true: started automatically from Vault → skip preview and close modal when status done */
  const handsFreeCloseRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const foundationFilledCount = foundationDiagnostics?.filledCount ?? 0;

  /* ───────────── Core POST — hints null for Vault auto-run ───────────── */

  const kickOffSetup = useCallback(async (rawTrimmed, hints) => {
    setSubmitError('');
    const trimmed = String(rawTrimmed || '').trim();
    if (trimmed.length < MIN_RAW) {
      setSubmitError(`Please describe your business in at least ${MIN_RAW} characters so the AI has enough context.`);
      return false;
    }

    setSubmitting(true);
    try {
      const resp = await authenticatedFetch(CLOUD_FUNCTION_URLS.setupRunAll(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          rawInfo: trimmed,
          hints: hints && typeof hints === 'object' && Object.keys(hints).length ? hints : null,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.runId) {
        throw new Error(data.error || 'Could not start setup');
      }
      setRunId(data.runId);
      setPhase('live');
      return true;
    } catch (err) {
      console.error('[UltimateSetup] start error:', err);
      setSubmitError(err.message || 'Could not start setup');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [userId]);

  /* ───────────── Bootstrap: enough Vault text → start immediately (no input / no preview) ───────────── */

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    handsFreeCloseRef.current = false;
    setPhase('bootstrap');

    (async () => {
      try {
        const snap = await get(ref(database, `World/${userId}/dataVault/entries`));
        if (cancelled) return;
        const seeded = snap.exists() ? buildVaultSeedRawInfo(snap.val()) : '';
        const trimmed = String(seeded).trim();

        if (trimmed.length >= MIN_RAW) {
          setVaultPrefilled(true);
          setRawInfo(trimmed);
          handsFreeCloseRef.current = true;
          const ok = await kickOffSetup(trimmed, null);
          if (cancelled) return;
          if (!ok) {
            handsFreeCloseRef.current = false;
            setPhase('input');
          }
          return;
        }

        setPhase('input');
        if (trimmed.length > 0) {
          setRawInfo(trimmed);
          setVaultPrefilled(true);
        }
      } catch (err) {
        console.warn('[UltimateSetup] bootstrap failed:', err);
        if (!cancelled) setPhase('input');
      }
    })();

    return () => { cancelled = true; };
  }, [userId, kickOffSetup]);

  /* ───────────── Phase 1 → 2: manual Start ───────────── */

  const startSetup = useCallback(async () => {
    const trimmed = rawInfo.trim();
    handsFreeCloseRef.current =
      trimmed.length >= MIN_RAW &&
      (vaultPrefilled || foundationFilledCount > 0);
    const h = {};
    if (hintName.trim()) h.name = hintName.trim();
    if (hintRole.trim()) h.role = hintRole.trim();
    if (hintCompany.trim()) h.company = hintCompany.trim();
    if (hintLanguage) h.language = hintLanguage;
    if (hintTargetSize) h.targetCompanySize = hintTargetSize;
    await kickOffSetup(trimmed, Object.keys(h).length ? h : null);
  }, [kickOffSetup, rawInfo, vaultPrefilled, foundationFilledCount, hintName, hintRole, hintCompany, hintLanguage, hintTargetSize]);

  /* ───────────── Phase 2: live polling via realtime listener ───────────── */

  useEffect(() => {
    if (!runId || !userId || phase === 'preview') return undefined;
    const r = ref(database, `World/${userId}/setupRuns/${runId}`);
    let handledTerminal = false;
    const handler = onValue(r, (snap) => {
      if (!snap.exists()) return;
      const v = snap.val();
      setRunState(v);
      if (v.status === 'failed') {
        handsFreeCloseRef.current = false;
      }
      if (v.status === 'done' && phase !== 'preview') {
        if (handledTerminal) return;
        handledTerminal = true;
        setTimeout(() => {
          if (handsFreeCloseRef.current) {
            onCloseRef.current?.();
          } else {
            setPhase('preview');
          }
        }, 600);
      }
    });
    // `onValue` (v9 modular) gibt direkt die Unsubscribe-Funktion zurück.
    // `off(ref, 'value', handler)` würde NICHTS detachen, weil `handler` hier
    // die Unsubscribe-Funktion ist und nicht der Daten-Callback.
    return () => handler();
  }, [runId, userId, phase]);

  /* ───────────── Phase 3 navigation helpers ───────────── */

  const handleOpenTopic = useCallback(() => {
    const topicId = runState?.finalIds?.topicId;
    if (topicId) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'topics');
      url.searchParams.set('topicId', topicId);
      window.history.replaceState(null, '', url.toString());
    }
    onOpenTab?.('topics');
    onClose?.();
  }, [runState, onOpenTab, onClose]);

  const handleOpenAi = useCallback(() => { onOpenTab?.('ai'); onClose?.(); }, [onOpenTab, onClose]);
  const handleOpenSafeGuard = useCallback(() => { onOpenTab?.('safeGuard'); onClose?.(); }, [onOpenTab, onClose]);
  const handleOpenVault = useCallback(() => { onOpenTab?.('dataVault'); onClose?.(); }, [onOpenTab, onClose]);

  /* ───────────── Derived UI bits ───────────── */

  const overallStatus = runState?.status || 'pending';
  const percent = Number(runState?.percent) || 0;
  const language = runState?.languageLabel;

  const stepRows = useMemo(() => {
    return STEPS_ORDER.map((s) => {
      const st = runState?.steps?.[s.id] || { status: 'pending', costCents: 0 };
      return { ...s, ...st };
    });
  }, [runState]);

  const failedStep = stepRows.find((s) => s.status === 'failed');

  /* ───────────── Render ───────────── */

  const isFullscreen = mode === 'fullscreen';
  const wrapperClass = isFullscreen ? 'usm__page' : 'usm__overlay';
  const innerClass = isFullscreen ? 'usm__pageInner' : 'usm__modal';
  const wrapperRole = isFullscreen ? undefined : 'dialog';
  const wrapperAriaModal = isFullscreen ? undefined : 'true';

  return (
    <div className={wrapperClass} role={wrapperRole} aria-modal={wrapperAriaModal}>
      <div className={innerClass} role={isFullscreen ? undefined : 'document'}>
        {isFullscreen ? (
          <div className="usm__pageBar">
            <button type="button" className="usm__backBtn" onClick={onClose} aria-label="Back to cockpit">
              <i className="fa fa-arrow-left" aria-hidden /> Back to cockpit
            </button>
          </div>
        ) : (
          <button type="button" className="usm__close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" aria-hidden />
          </button>
        )}

        {phase === 'bootstrap' && (
          <div className="usm__phase usm__phase--bootstrap">
            <div className="usm__bootstrapInner">
              <i className="fa fa-circle-notch fa-spin usm__bootstrapSpinner" aria-hidden />
              <p className="usm__bootstrapTitle">Checking your Account Vault…</p>
              <p className="usm__bootstrapHint">
                If your Vault already has enough context from setup or notes, we start automatically: no form and no summary screen at the end.
              </p>
            </div>
          </div>
        )}

        {phase === 'input' && (
          <PhaseInput
            rawInfo={rawInfo} setRawInfo={setRawInfo}
            hintName={hintName} setHintName={setHintName}
            hintRole={hintRole} setHintRole={setHintRole}
            hintCompany={hintCompany} setHintCompany={setHintCompany}
            hintLanguage={hintLanguage} setHintLanguage={setHintLanguage}
            hintTargetSize={hintTargetSize} setHintTargetSize={setHintTargetSize}
            submitError={submitError} submitting={submitting}
            foundationDiagnostics={foundationDiagnostics}
            onOpenTab={onOpenTab}
            vaultPrefilled={vaultPrefilled}
            onStart={startSetup}
          />
        )}

        {phase === 'live' && (
          <PhaseLive
            stepRows={stepRows}
            overallStatus={overallStatus}
            percent={percent}
            language={language}
            failedStep={failedStep}
            failureMessage={runState?.failureMessage}
            expandedStep={expandedStep}
            onToggleStep={(id) => setExpandedStep((p) => p === id ? null : id)}
            onClose={onClose}
          />
        )}

        {phase === 'preview' && (
          <PhasePreview
            runState={runState}
            language={language}
            onOpenTopic={handleOpenTopic}
            onOpenAi={handleOpenAi}
            onOpenSafeGuard={handleOpenSafeGuard}
            onOpenVault={handleOpenVault}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────── PHASE 1 ──────────────────────────── */

function PhaseInput(props) {
  const {
    rawInfo, setRawInfo,
    hintName, setHintName, hintRole, setHintRole,
    hintCompany, setHintCompany, hintLanguage, setHintLanguage,
    hintTargetSize, setHintTargetSize,
    submitError, submitting, onStart,
    foundationDiagnostics,
    onOpenTab,
    vaultPrefilled,
  } = props;
  const { openCapture } = useSpeakEngine();
  const charCount = rawInfo.length;
  const tooShort = charCount < MIN_RAW;
  const fd = foundationDiagnostics;

  // "Speak instead" — opens the global engine with the businessSetup preset.
  // Existing typed values are forwarded as context so the AI merges instead
  // of blindly overwriting. The onResult callback pipes each extracted field
  // straight into the local state setters.
  const handleSpeakInstead = useCallback(() => {
    openCapture({
      preset: 'businessSetup',
      context: {
        existingValues: {
          rawInfo: rawInfo || '',
          companyName: hintCompany || '',
          name: hintName || '',
          role: hintRole || '',
          language: hintLanguage || '',
          targetSize: hintTargetSize || '',
        },
      },
      onResult: async (values) => {
        // Only overwrite fields the AI actually produced a value for.
        // Empty strings mean "don't set" (respect what the user already typed).
        if (values.rawInfo && values.rawInfo.trim())      setRawInfo(values.rawInfo.trim());
        if (values.companyName && values.companyName.trim()) setHintCompany(values.companyName.trim());
        if (values.name && values.name.trim())             setHintName(values.name.trim());
        if (values.role && values.role.trim())             setHintRole(values.role.trim());
        if (values.language)                               setHintLanguage(values.language);
        if (values.targetSize)                             setHintTargetSize(values.targetSize);
        return 'Setup prefilled — review and launch.';
      },
    });
  }, [openCapture, rawInfo, hintCompany, hintName, hintRole, hintLanguage, hintTargetSize, setRawInfo, setHintCompany, setHintName, setHintRole, setHintLanguage, setHintTargetSize]);

  return (
    <div className="usm__phase usm__phase--input">
      <header className="usm__header">
        <span className="usm__badge"><i className="fa fa-bolt" /> Powered by BonusClub AI</span>
        <h2 className="usm__title">Ultimate Setup</h2>
        <p className="usm__subtitle">
          Tell SalesGod what you do and who you want to reach. In ~2 minutes you get your Vault filled,
          Cleaner rules, SafeGuard configured, and one ready-to-send email template with 3 polished variants, auto-activated.
        </p>
      </header>

      {vaultPrefilled && (
        <div className="usm__vaultSeedBanner" role="status">
          <i className="fa fa-database" aria-hidden />
          <div>
            <strong>Loaded from your Account Vault</strong>
            <span>: Notes & Files (long-term brain). Edit anything below before starting.</span>
            {onOpenTab && (
              <button type="button" className="usm__vaultSeedLink" onClick={() => onOpenTab('dataVault')}>
                Open Vault
              </button>
            )}
          </div>
        </div>
      )}

      {fd && fd.allDone && (
        <div className="usm__foundationPanel usm__foundationPanel--ok">
          <div className="usm__foundationPanelTitle"><i className="fa fa-circle-check" aria-hidden /> Foundation looks complete</div>
          <p className="usm__foundationPanelMeta">
            Vault, Cleaner, SafeGuard, templates, and contacts are already present. You can still run setup to refresh everything from a new description.
          </p>
        </div>
      )}

      {fd && !fd.allDone && fd.missing.length > 0 && (
        <div className="usm__foundationPanel usm__foundationPanel--attention">
          <div className="usm__foundationPanelTitle"><i className="fa fa-magnifying-glass-chart" aria-hidden /> Detected in your account</div>
          <p className="usm__foundationPanelMeta">
            <strong>{fd.filledCount}/{fd.totalCount}</strong> foundation items already look good.
            Still open: {fd.missing.map((m) => m.label).join(' · ')}.
            {' '}The pipeline regenerates Vault, Cleaner, SafeGuard, and templates together from your text so everything stays coherent.
            {' '}
            {fd.checks.contacts?.ok ? (
              <span><strong>Lead batch:</strong> skipped automatically, since you already have contacts in your database.</span>
            ) : (
              <span><strong>Lead batch:</strong> runs after setup unless you already have contacts.</span>
            )}
          </p>
          <ul className="usm__foundationList">
            {fd.missing.map((m) => (
              <li key={m.id}>
                <span className="usm__foundationMiss"><i className="fa fa-circle-o" aria-hidden /> {m.label}</span>
                {onOpenTab && (
                  <button type="button" className="usm__foundationJump" onClick={() => onOpenTab(m.tab)}>Open tab</button>
                )}
              </li>
            ))}
          </ul>
          {fd.filled.length > 0 && (
            <details className="usm__foundationOkDetails">
              <summary>Already in place ({fd.filled.length})</summary>
              <ul className="usm__foundationList usm__foundationList--ok">
                {fd.filled.map((m) => (
                  <li key={m.id}><i className="fa fa-check" aria-hidden /> {m.label}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="usm__senderBlock">
        <div className="usm__senderHeader">
          <span className="usm__senderTitle">
            <i className="fa fa-paper-plane" aria-hidden /> Email sender persona
          </span>
          <span className="usm__senderNote">
            Whose name will appear as the sender of every outgoing email. <strong>Not your name as account manager</strong>. Enter the person these emails should come from (e.g. the founder, sales lead, or account owner).
          </span>
        </div>
        <div className="usm__hintsGrid">
          <label className="usm__hint">
            <span className="usm__hintLabel">Sender's full name</span>
            <input
              type="text"
              value={hintName}
              onChange={(e) => setHintName(e.target.value)}
              placeholder="e.g. Max Mustermann"
            />
          </label>
          <label className="usm__hint">
            <span className="usm__hintLabel">Sender's role / title</span>
            <input
              type="text"
              value={hintRole}
              onChange={(e) => setHintRole(e.target.value)}
              placeholder="e.g. Founder, CEO, Head of Sales"
            />
          </label>
          <label className="usm__hint usm__hint--wide">
            <span className="usm__hintLabel">Sender's company</span>
            <input
              type="text"
              value={hintCompany}
              onChange={(e) => setHintCompany(e.target.value)}
              placeholder="e.g. Nordwind Consulting GmbH"
            />
          </label>
        </div>
      </div>

      <div className="usm__labelRow">
        <span className="usm__labelText">Tell us about your business and target audience</span>
        <button
          type="button"
          className="usm__speakBtn"
          onClick={handleSpeakInstead}
          title="Dictate — AI will structure your words and prefill every field on this screen."
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <path d="M12 19v3" />
          </svg>
          Speak instead
        </button>
      </div>
      <label className="usm__label">
        <textarea
          className="usm__textarea"
          rows={9}
          value={rawInfo}
          onChange={(e) => setRawInfo(e.target.value)}
          placeholder={`Example:\nWir sind BonusClub GmbH aus München. Wir bauen AI-Software für Cold Outreach. Unsere Zielgruppe sind Marketing-Agenturen mit 5–50 Mitarbeitern in DACH, die mehr Pipeline brauchen aber keine eigenen SDRs leisten können. Tonalität: direkt, ohne Marketing-Floskeln.`}
        />
        <div className="usm__charCount">
          <span className={tooShort ? 'usm__charCount--low' : 'usm__charCount--ok'}>
            {charCount} / {MIN_RAW}+ characters
          </span>
          {!tooShort && <span className="usm__charCountOk"><i className="fa fa-check-circle" /> ready</span>}
        </div>
      </label>

      <div className="usm__hintsGrid">
        <label className="usm__hint">
          <span className="usm__hintLabel">Output language (optional)</span>
          <select value={hintLanguage} onChange={(e) => setHintLanguage(e.target.value)}>
            {LANGUAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="usm__hint usm__hint--wide">
          <span className="usm__hintLabel">Target company size (drives lead research)</span>
          <select value={hintTargetSize} onChange={(e) => setHintTargetSize(e.target.value)}>
            {COMPANY_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      {submitError && <div className="usm__error"><i className="fa fa-triangle-exclamation" /> {submitError}</div>}

      <div className="usm__actions usm__actions--right">
        <button
          type="button"
          className="usm__btn usm__btn--primary"
          disabled={tooShort || submitting}
          onClick={onStart}
        >
          {submitting ? <><i className="fa fa-circle-notch fa-spin" /> Starting…</> : <>{(foundationDiagnostics?.filledCount ?? 0) > 0 ? 'Continue Ultimate Setup' : 'Start Ultimate Setup'} <i className="fa fa-arrow-right" /></>}
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────── PHASE 2 ──────────────────────────── */

function PhaseLive(props) {
  const {
    stepRows, overallStatus, percent, language,
    failedStep, failureMessage, expandedStep, onToggleStep, onClose,
  } = props;
  const isFailed = overallStatus === 'failed';

  return (
    <div className="usm__phase usm__phase--live">
      <header className="usm__header">
        <span className={`usm__badge usm__badge--${isFailed ? 'fail' : 'live'}`}>
          {isFailed ? <><i className="fa fa-triangle-exclamation" /> Setup interrupted</> : <><i className="fa fa-circle-notch fa-spin" /> Setup running</>}
        </span>
        <h2 className="usm__title">{isFailed ? 'Something went wrong' : 'SalesGod is building your foundation'}</h2>
        {language && !isFailed && (
          <p className="usm__subtitle">Detected language: <strong>{language}</strong>. All outputs locked to this language.</p>
        )}
      </header>

      <div className="usm__progressOuter">
        <div className="usm__progressTrack">
          <div className="usm__progressBar" style={{ width: `${Math.max(2, percent)}%` }} />
        </div>
        <div className="usm__progressMeta">
          <span><strong>{percent}%</strong> complete</span>
        </div>
      </div>

      <ul className="usm__steps">
        {stepRows.map((s) => {
          const isExpanded = expandedStep === s.id;
          const hasThinking = s.thinkingSummary && s.thinkingSummary.length > 0;
          const hasOutput = s.output && typeof s.output === 'object';
          const canExpand = hasThinking || hasOutput || s.errorMessage;
          return (
            <li key={s.id} className={`usm__step usm__step--${s.status}`}>
              <button
                type="button"
                className="usm__stepRow"
                onClick={canExpand ? () => onToggleStep(s.id) : undefined}
                disabled={!canExpand}
              >
                <span className="usm__stepIcon">
                  {s.status === 'done' && s.output?.skipped && <i className="fa fa-forward" title="Skipped" />}
                  {s.status === 'done' && !s.output?.skipped && <i className="fa fa-circle-check" />}
                  {s.status === 'running' && <i className="fa fa-circle-notch fa-spin" />}
                  {s.status === 'pending' && <i className="fa fa-circle" />}
                  {s.status === 'failed' && <i className="fa fa-circle-xmark" />}
                </span>
                <span className="usm__stepLabel">
                  <i className={`fa ${s.icon}`} aria-hidden /> {s.label}
                </span>
                {canExpand && (
                  <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} usm__stepCaret`} />
                )}
              </button>
              {isExpanded && (
                <div className="usm__stepDetail">
                  {hasOutput && (
                    <div className="usm__stepOutput">
                      {Object.entries(s.output).map(([k, v]) => (
                        <div key={k}><strong>{k}:</strong> {Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}</div>
                      ))}
                    </div>
                  )}
                  {hasThinking && (
                    <details className="usm__thinking" open={!hasOutput}>
                      <summary><i className="fa fa-brain" /> Why the AI decided this</summary>
                      <pre>{s.thinkingSummary}</pre>
                    </details>
                  )}
                  {s.errorMessage && <div className="usm__stepError">{s.errorMessage}</div>}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {isFailed && (
        <div className="usm__failBlock">
          <div className="usm__failTitle"><i className="fa fa-triangle-exclamation" /> Setup failed during {failedStep?.label || 'an unknown step'}</div>
          {failureMessage && <p className="usm__failMessage">{failureMessage}</p>}
          <p className="usm__failHelp">Your existing settings were not changed. You can close this and try again. Your input has been preserved.</p>
          <div className="usm__actions usm__actions--right">
            <button type="button" className="usm__btn" onClick={onClose}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── PHASE 3 ──────────────────────────── */

function PhasePreview(props) {
  const { runState, language, onOpenTopic, onOpenAi, onOpenSafeGuard, onOpenVault, onClose } = props;
  const summary = runState?.summary || {};
  const finalIds = runState?.finalIds || {};
  const profile = runState?.results?.profile || null;
  const variantsOut = Array.isArray(runState?.steps?.variants?.output?.variantIds) ? runState.steps.variants.output.variantIds : [];
  const leadsStep = runState?.steps?.leads || null;
  const leadsCount = Number(leadsStep?.output?.count) || 0;
  const leadsSkipped = leadsStep?.output?.skipped === true;
  const leadsStatus = leadsStep?.status;

  return (
    <div className="usm__phase usm__phase--preview">
      <header className="usm__header">
        <span className="usm__badge usm__badge--success"><i className="fa fa-circle-check" /> Setup complete</span>
        <h2 className="usm__title">Your foundation is ready</h2>
        <p className="usm__subtitle">
          {language && <>Language: <strong>{language}</strong></>}
          {language && profile?.target?.audience?.industryDescription && <> · </>}
          {profile?.target?.audience?.industryDescription && <>Target: <strong>{profile.target.audience.industryDescription}</strong></>}
        </p>
      </header>

      <div className="usm__previewGrid">
        <div className="usm__previewCard">
          <div className="usm__previewCardIcon"><i className="fa fa-database" /></div>
          <div className="usm__previewCardBody">
            <h4>Long-term Brain</h4>
            <p>5 entries written to your Account Vault: Brand & Voice, Target Audience, Offer & USPs, Goals, Setup Notes.</p>
          </div>
          <button type="button" className="usm__btn" onClick={onOpenVault}>Open Vault →</button>
        </div>

        <div className="usm__previewCard">
          <div className="usm__previewCardIcon"><i className="fa fa-filter" /></div>
          <div className="usm__previewCardBody">
            <h4>Cleaner Rules</h4>
            <p>Keep/Remove guidelines tuned for your target, set as the default cleaner.</p>
          </div>
          <button type="button" className="usm__btn" onClick={onOpenAi}>Open AI Settings →</button>
        </div>

        <div className="usm__previewCard">
          <div className="usm__previewCardIcon"><i className="fa fa-shield-halved" /></div>
          <div className="usm__previewCardBody">
            <h4>SafeGuard</h4>
            <p>Dos / Don'ts / Target Audience / Important Notes configured. Threshold: <strong>6</strong>.</p>
          </div>
          <button type="button" className="usm__btn" onClick={onOpenSafeGuard}>Open SafeGuard →</button>
        </div>

        <div className="usm__previewCard usm__previewCard--highlight">
          <div className="usm__previewCardIcon"><i className="fa fa-pen-to-square" /></div>
          <div className="usm__previewCardBody">
            <h4>Email template (auto-activated)</h4>
            <p>
              <strong>{summary?.topicId ? `${runState?.results?.strategy?.templateName || 'Starter'} ` : ''}</strong>
              with <strong>3</strong> polished variants ready to send. Hyper-Intelligence + Smart-Greeting on. Model: Claude Haiku.
            </p>
            {variantsOut.length > 0 && (
              <div className="usm__previewVariants">
                {variantsOut.map((id) => <span key={id} className="usm__previewVariantPill">{id}</span>)}
              </div>
            )}
          </div>
          <button type="button" className="usm__btn usm__btn--primary" onClick={onOpenTopic} disabled={!finalIds.topicId}>
            Review Templates →
          </button>
        </div>

        {(leadsStatus === 'running' || leadsStatus === 'done' || leadsStatus === 'failed') && (
          <div className="usm__previewCard">
            <div className="usm__previewCardIcon"><i className="fa fa-user-plus" /></div>
            <div className="usm__previewCardBody">
              <h4>Prepared Leads <span className="usm__previewBadge">Beta</span></h4>
              {leadsStatus === 'running' && (
                <p><i className="fa fa-circle-notch fa-spin" /> Researching {leadsStep?.output?.targetCount || 15} matching companies via web search…</p>
              )}
              {leadsStatus === 'done' && leadsSkipped && (
                <p>{leadsStep?.output?.reason || 'Lead batch was skipped.'}</p>
              )}
              {leadsStatus === 'done' && !leadsSkipped && (
                <p><strong>{leadsCount}</strong> leads researched and queued. Open WorldDb to review and add them to your contact list.</p>
              )}
              {leadsStatus === 'failed' && (
                <p>Lead research could not finish. Your setup is unaffected. You can rerun lead generation later.</p>
              )}
            </div>
            <button type="button" className="usm__btn" onClick={onClose}>Open WorldDb →</button>
          </div>
        )}
      </div>

      <div className="usm__actions usm__actions--right">
        <button type="button" className="usm__btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
